<script setup lang="ts">
import { ref, reactive } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { FireIcon, HomeIcon, MoonIcon, SunIcon } from '@heroicons/vue/24/outline';
import SidebarLayout from '@/layout/SidebarLayout.vue';
import ImmersveLogo from '@/assets/ImmersveLogo.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import InputConfigValue from '@/components/ui/InputConfigValue.vue';
import RecommendationChip from '@/components/aml-review/RecommendationChip.vue';
import ProviderBadge from '@/components/aml-review/ProviderBadge.vue';
import HitTypeTag from '@/components/aml-review/HitTypeTag.vue';
import QueueTable from '@/components/aml-review/QueueTable.vue';

const knownHitTypes = ['SAN', 'PEP', 'AM', 'WARN', 'WL', 'F&P'];

function onButtonClick(label: string) {
  // eslint-disable-next-line no-console
  console.info(`clicked: ${label}`);
}

const demoValues = reactive({
  relish: 'Chicago-style, all the way',
  splitTheBun: true,
  toppingCount: '7',
});

const dialogOpen = ref(false);
</script>

<template>
  <SidebarLayout>
    <div class="mx-auto max-w-3xl px-6 py-12" data-test="screen-components">
      <h1 class="mb-2 text-2xl font-bold text-gray-900">Component Demo</h1>
      <p class="mb-10 text-sm text-gray-500">
        A sandbox for the shared UI pieces currently in the repo — heroicons, HeadlessUI, and the
        two <code class="text-xs">components/ui</code> primitives ported alongside the Config page.
      </p>

      <section class="mb-10">
        <h2 class="mb-4 text-lg font-semibold text-gray-900">Brand</h2>
        <div class="flex items-center gap-4">
          <ImmersveLogo class="h-12 w-12" />
          <p class="text-sm text-gray-500">
            Ketchup is a valid condiment. Mustard is not optional.
          </p>
        </div>
      </section>

      <section class="mb-10">
        <h2 class="mb-4 text-lg font-semibold text-gray-900">Icons (heroicons/24/outline)</h2>
        <div class="flex flex-wrap gap-6">
          <div class="flex flex-col items-center gap-1">
            <SunIcon class="size-6 text-gray-700" />
            <span class="text-xs text-gray-500">SunIcon</span>
          </div>
          <div class="flex flex-col items-center gap-1">
            <MoonIcon class="size-6 text-gray-700" />
            <span class="text-xs text-gray-500">MoonIcon</span>
          </div>
          <div class="flex flex-col items-center gap-1">
            <HomeIcon class="size-6 text-gray-700" />
            <span class="text-xs text-gray-500">HomeIcon</span>
          </div>
          <div class="flex flex-col items-center gap-1">
            <FireIcon class="size-6 text-gray-700" />
            <span class="text-xs text-gray-500">FireIcon (relish, presumably)</span>
          </div>
        </div>
      </section>

      <section class="mb-10">
        <h2 class="mb-4 text-lg font-semibold text-gray-900">BaseButton</h2>
        <div class="flex flex-wrap gap-4">
          <BaseButton text="Grill it" @click="onButtonClick('Grill it')" data-test="demo-btn-1" />
          <BaseButton
            text="Boil it (fight me)"
            @click="onButtonClick('Boil it')"
            data-test="demo-btn-2"
          />
          <BaseButton
            text="Add relish"
            @click="onButtonClick('Add relish')"
            data-test="demo-btn-3"
          />
        </div>
      </section>

      <section class="mb-10">
        <h2 class="mb-4 text-lg font-semibold text-gray-900">InputConfigValue</h2>
        <div class="max-w-sm rounded-lg border border-gray-300 bg-white px-4">
          <InputConfigValue
            item-key="relish"
            display-value="Preferred Topping"
            v-model="demoValues.relish"
            :current-value="demoValues.relish"
            base-value="Plain, sadly"
            :read-only="false"
          />
          <InputConfigValue
            item-key="splitTheBun"
            display-value="Split The Bun"
            v-model="demoValues.splitTheBun"
            :current-value="demoValues.splitTheBun"
            :base-value="false"
            :read-only="false"
          />
          <InputConfigValue
            item-key="toppingCount"
            display-value="Topping Count (read only)"
            v-model="demoValues.toppingCount"
            :current-value="demoValues.toppingCount"
            base-value="7"
            :read-only="true"
          />
        </div>
      </section>

      <section>
        <h2 class="mb-4 text-lg font-semibold text-gray-900">HeadlessUI Dialog</h2>
        <BaseButton text="Open the bun" @click="dialogOpen = true" data-test="demo-dialog-open" />

        <TransitionRoot as="template" :show="dialogOpen">
          <Dialog class="relative z-50" @close="dialogOpen = false">
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0"
              enter-to="opacity-100"
              leave="ease-in duration-200"
              leave-from="opacity-100"
              leave-to="opacity-0"
            >
              <div class="fixed inset-0 bg-gray-900/40" />
            </TransitionChild>

            <div class="fixed inset-0 flex items-center justify-center p-4">
              <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0 scale-95"
                enter-to="opacity-100 scale-100"
                leave="ease-in duration-200"
                leave-from="opacity-100 scale-100"
                leave-to="opacity-0 scale-95"
              >
                <DialogPanel class="w-full max-w-sm rounded-lg bg-white p-6">
                  <DialogTitle class="text-lg font-semibold text-gray-900">
                    A wild hot dog appears
                  </DialogTitle>
                  <p class="mt-2 text-sm text-gray-500">
                    Ketchup on a hot dog past the age of eighteen is a warning sign, not a condiment
                    choice. This dialog is just proving HeadlessUI works outside
                    <code class="text-xs">SidebarLayout</code>'s own mobile nav usage.
                  </p>
                  <div class="mt-4">
                    <BaseButton
                      text="Close"
                      @click="dialogOpen = false"
                      data-test="demo-dialog-close"
                    />
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </Dialog>
        </TransitionRoot>
      </section>

      <section class="mb-10">
        <h2 class="mb-4 text-lg font-semibold text-gray-900">RecommendationChip</h2>
        <p class="mb-4 text-sm text-gray-500">The review's status on a queue row.</p>
        <div class="flex flex-wrap gap-4">
          <RecommendationChip recommendation="CREATED" />
          <RecommendationChip recommendation="PROCESSING" />
          <RecommendationChip recommendation="REVIEW" />
          <RecommendationChip recommendation="COMPLETE" />
          <RecommendationChip recommendation="CANCELED" />
        </div>
      </section>

      <section class="mb-10">
        <h2 class="mb-4 text-lg font-semibold text-gray-900">ProviderBadge</h2>
        <p class="mb-4 text-sm text-gray-500">The KYC provider that ran the check.</p>
        <div class="flex flex-wrap gap-4">
          <ProviderBadge provider="frankieOne" />
          <ProviderBadge provider="sumsub" />
        </div>
      </section>

      <section class="mb-10">
        <h2 class="mb-4 text-lg font-semibold text-gray-900">HitTypeTag</h2>
        <p class="mb-4 text-sm text-gray-500">
          Every known hit type, plus an unrecognized string (<code class="text-xs">XYZ</code>) to
          demonstrate the fallback to rendering the raw value.
        </p>
        <div class="flex flex-wrap gap-2">
          <HitTypeTag v-for="hitType in knownHitTypes" :key="hitType" :hit-type="hitType" />
          <HitTypeTag hit-type="XYZ" />
        </div>
      </section>

      <section class="mb-10">
        <h2 class="mb-4 text-lg font-semibold text-gray-900">QueueTable</h2>
        <p class="mb-4 text-sm text-gray-500">
          Fetches from <code class="text-xs">AmethystClient</code> — needs a real backend/API key to
          show data.
        </p>
        <QueueTable status="escalated" />
      </section>
    </div>
  </SidebarLayout>
</template>
